import { captureError } from "../ErrorHandlers"
import {
  GenericMessage,
  GroupableRow,
  isGroupedRow,
  Message,
  MessageDisplayType,
  MessageGroup,
} from "../reducers/messagesSlice"
import { parse as jidParse } from "./cachedJid"
import type { JID } from "@xmpp/jid"
import type { AccountChat } from "./accountChatsParser"
import { getTransicsMessagePresentation } from "./transicsMessagePresentation"
import { getIntlLocale } from "./i18n/format"

export type MessageStatus =
  | "scheduled"
  | "not_sent"
  | "sent"
  | "received"
  | "displayed"

const dateFormatters = new Map<string, Intl.DateTimeFormat>()

// Built at call time, so the date follows the interface language.
const dateFormatter = (): Intl.DateTimeFormat => {
  const locale = getIntlLocale("en-GB") ?? "en-GB"
  let formatter = dateFormatters.get(locale)
  if (!formatter) {
    formatter = new Intl.DateTimeFormat(locale)
    dateFormatters.set(locale, formatter)
  }
  return formatter
}

class MessageHelpers {
  static applyCorrection(original: Message, correction: Message): Message {
    return {
      ...original,
      body: correction.body,
      file: correction.file,
      url: correction.url,
      fileName: correction.fileName,
      references: correction.references,
      translation: correction.translation,
      originalBody: correction.originalBody,
      originalBodyLang: correction.originalBodyLang,
      thumbnail: correction.thumbnail,
    }
  }

  static isSendingOrError(message: GenericMessage): boolean {
    return (
      "sendStatus" in message &&
      (message.sendStatus === "sending" || message.sendStatus === "error")
    )
  }

  static hasComeFromJid(
    messageOrMessageGroup: GenericMessage | MessageGroup,
    compareJid?: string,
  ): boolean {
    if (messageOrMessageGroup.displayType === MessageDisplayType.MessageGroup) {
      return false
    }

    const message = messageOrMessageGroup as GenericMessage

    if (!compareJid) return false
    const theJid = jidParse(compareJid)
    return (
      MessageHelpers.fromBareJid(message, theJid.domain) ===
      theJid.bare().toString()
    )
  }

  static senderJid(message: GenericMessage): string | undefined {
    return "senderJid" in message ? message.senderJid : undefined
  }

  static senderKey(message: GenericMessage): string {
    return MessageHelpers.senderJid(message) ?? message.from
  }

  //For groupochats, message.from is always a MUC JID, with the resource part being the nickname of the sender.
  //Thats why we need to extract the resource part and append the domain part to get the actual from jid.
  static fromBareJid(message: GenericMessage, domain: string): string {
    const senderJid = MessageHelpers.senderJid(message)
    if (senderJid) return senderJid
    const fromJid = jidParse(message.from)
    if (this.isSendingOrError(message)) {
      return fromJid.bare().toString()
    }

    if (this.isFromForwarder(message)) {
      return `${fromJid.getLocal()}@${domain}`
    }

    if (message.type === "groupchat") {
      return `${fromJid.getResource()}@${domain}`
    }

    return fromJid.bare().toString()
  }

  static isFromForwarder(message: Pick<GenericMessage, "from">): boolean {
    return jidParse(message.from)?.getResource() === "forwarder"
  }

  static userName(message: GenericMessage): string {
    return message.type === "groupchat"
      ? jidParse(message.from)?.getResource()
      : jidParse(message.from)?.getLocal()
  }

  static isIncoming(message: GenericMessage): boolean {
    if (!message.from || !message.to) {
      return false
    }
    const senderJid = MessageHelpers.senderJid(message)
    if (senderJid) return senderJid !== jidParse(message.to).bare().toString()
    return jidParse(message.from).resource !== jidParse(message.to).local
  }

  static isFromCommonInboxPerson(
    message: GenericMessage,
    commonInboxNicks: string[],
  ): boolean {
    return commonInboxNicks.includes(jidParse(message.from).resource)
  }

  static isDisplayed(message: Message, resources: string[]): boolean {
    return this.isMarked(message, resources, "displayed")
  }

  static isReceived(message: Message, resources: string[]): boolean {
    return this.isMarked(message, resources, "received")
  }

  static isMarked(
    message: Message,
    resources: string[],
    markerName: string,
  ): boolean {
    return message.markers
      .filter((marker) => resources.includes(jidParse(marker.from).resource))
      .some((marker) => marker.name === markerName)
  }

  static isToMyself(message: GenericMessage): boolean {
    return (
      message.type === "chat" &&
      jidParse(message.from).bare().toString() ===
        jidParse(message.to).bare().toString()
    )
  }

  static timestamp(message: Message, withDate = true): string {
    const date = new Date(message.timestamp)

    const time = date.toTimeString().slice(0, 5)

    if (withDate) {
      return `${time} ${dateFormatter().format(date)}`
    }

    return time
  }

  static shouldGroup(
    messageOrMessageGroup: GroupableRow,
    prevMessageOrMessageGroup?: GroupableRow,
    accountChat?: AccountChat,
  ): boolean {
    if (!prevMessageOrMessageGroup) return false

    if (
      isGroupedRow(messageOrMessageGroup) ||
      isGroupedRow(prevMessageOrMessageGroup)
    ) {
      return false
    }

    const message = messageOrMessageGroup as Message
    const prevMessage = prevMessageOrMessageGroup as Message

    const FIVE_MINUTES = 5 * 60

    if (message.from !== prevMessage.from) return false
    if (
      MessageHelpers.senderKey(message) !==
      MessageHelpers.senderKey(prevMessage)
    )
      return false
    const author =
      getTransicsMessagePresentation(message, accountChat)?.authorName ??
      message.author
    const prevAuthor =
      getTransicsMessagePresentation(prevMessage, accountChat)?.authorName ??
      prevMessage.author
    if (author !== prevAuthor) return false

    const secondsDiff =
      (Date.parse(message.timestamp) - Date.parse(prevMessage.timestamp)) / 1000
    return Math.abs(secondsDiff) < FIVE_MINUTES
  }

  static applyCorrections<T extends Message | MessageGroup>(
    messageOrMessageGroup: T,
    messageCorrections: Message[],
  ): T {
    const applyMessageCorrection = (
      message: Message,
      messageCorrections: Message[],
    ) => {
      const correction = messageCorrections
        ? messageCorrections.find((corr) => corr.replaceId === message.id)
        : undefined
      if (correction) {
        return MessageHelpers.applyCorrection(message, correction)
      }

      return message
    }

    if (messageOrMessageGroup.displayType === MessageDisplayType.MessageGroup) {
      const messageGroup = messageOrMessageGroup as MessageGroup
      messageGroup.messages = messageGroup.messages.map((message) =>
        applyMessageCorrection(message, messageCorrections),
      )
      return messageGroup as T
    }

    return applyMessageCorrection(
      messageOrMessageGroup as Message,
      messageCorrections,
    ) as T
  }

  static markRetracted = (message: Message) => {
    return {
      ...message,
      retracted: true,
    }
  }

  static getBodyWithAppliedAddressReferences(
    message: Message,
    wrapAddressString: (address: string) => string,
  ): string {
    if (!message.references || message.references?.length === 0)
      return message.body

    try {
      const chunks = []
      let copyFromIndex = 0
      const references = message.references
        .slice()
        .sort((a, b) => a.begin - b.begin)
      references.forEach((reference) => {
        const begin = reference.begin
        const end = reference.end

        chunks.push(message.body.slice(copyFromIndex, begin))
        const dist = message.body.slice(begin, end)
        chunks.push(wrapAddressString(dist))

        copyFromIndex = end
      })

      // add the rest of the message
      if (copyFromIndex < message.body.length - 1) {
        chunks.push(message.body.slice(copyFromIndex, message.body.length))
      }

      return chunks.join("")
    } catch (error) {
      captureError(error, {
        origin: "Other",
        extra: { message: "getBodyWithAppliedAddressReferences" },
      })
      return message.body
    }
  }

  static reactionsForDisplay(message: Message, myJid: JID, driverJid?: string) {
    return message.reactions.flatMap((reaction) =>
      reaction.emojis.map((emoji) => ({
        emoji,
        count: 1,
        isOwnReaction: MessageHelpers.hasComeFromJid(
          reaction,
          myJid.toString(),
        ),
        isDriverReaction: MessageHelpers.hasComeFromJid(reaction, driverJid),
      })),
    )
  }

  static getReactionsAsEmojisWithSender(message: Message) {
    return message.reactions.flatMap((r) =>
      r.emojis.map((emoji) => ({
        from: r.from,
        emoji,
      })),
    )
  }
}

export default MessageHelpers
